import { Transaction, Block, NetworkStats } from '../models';
import { logger } from '../utils/Logger';

export interface BitcoinEvent {
  type: 'transaction' | 'block' | 'stats_update' | 'connection_status';
  timestamp: number;
  data: any;
}

export interface TransactionEvent extends BitcoinEvent {
  type: 'transaction';
  data: Transaction;
}

export interface BlockEvent extends BitcoinEvent {
  type: 'block';
  data: Block;
}

export interface StatsUpdateEvent extends BitcoinEvent {
  type: 'stats_update';
  data: NetworkStats;
}

export interface ConnectionStatusEvent extends BitcoinEvent {
  type: 'connection_status';
  data: {
    source: 'mempool' | 'blockchain';
    status: 'connected' | 'disconnected' | 'error';
    message?: string;
  };
}

type EventHandler<T = any> = (event: T) => void;

export class EventBus {
  private handlers: Map<string, Set<EventHandler>> = new Map();

  emit(event: BitcoinEvent): void {
    const handlers = this.handlers.get(event.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          logger.error(`Error in event handler for ${event.type}`, 'EventBus', error as Error);
        }
      });
    }
  }

  on<T extends BitcoinEvent>(type: T['type'], handler: EventHandler<T>): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);
  }

  off<T extends BitcoinEvent>(type: T['type'], handler: EventHandler<T>): void {
    const handlers = this.handlers.get(type);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.handlers.delete(type);
      }
    }
  }

  once<T extends BitcoinEvent>(type: T['type'], handler: EventHandler<T>): void {
    const onceHandler = (event: T) => {
      handler(event);
      this.off(type, onceHandler);
    };
    this.on(type, onceHandler);
  }

  removeAllListeners(type?: string): void {
    if (type) {
      this.handlers.delete(type);
    } else {
      this.handlers.clear();
    }
  }

  listenerCount(type: string): number {
    const handlers = this.handlers.get(type);
    return handlers ? handlers.size : 0;
  }
}

export const eventBus = new EventBus();